import useProductGroupOptions from 'Services/ProductManagement/Features/Parameter/Hooks/useProductGroupOptions'
import type { Product } from 'Services/ProductManagement/Features/Product/types'
import getTranslation from 'Util/getTranslation'
import type { SelectChangeEvent } from '@mui/material'
import { useState } from 'react'

interface UseProductsEditReturn {
  searchText: string
  productGroupFilter: string
  productGroupOptions: Array<{ label: string; value: string }> | undefined
  isProductGroupParameterGroupLoading: boolean
  handleSearchTextChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  handleProductGroupFilterChange: (event: SelectChangeEvent) => void
  filterOptions: (optionsToFilter: Product[]) => Product[]
}

/**
 * Custom hook that provides common functionality for product transfer list.
 *
 * This hook encapsulates shared logic for:
 * - Search functionality with text input handling
 * - Product group filtering with dropdown selection
 * - Combined filtering operations (search + product group)
 * - Product group options management from parameter groups
 *
 */
export function useProductsEdit(pta: string): UseProductsEditReturn {
  const { productGroupOptions, isProductGroupParameterGroupLoading } = useProductGroupOptions(pta)

  const [searchText, setSearchText] = useState('')
  const [productGroupFilter, setProductGroupFilter] = useState('')

  const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value)
  }

  const handleProductGroupFilterChange = (event: SelectChangeEvent) => {
    const newValue = event.target.value
    setProductGroupFilter(newValue)
  }

  /**
   * Filters the given array of products based on the search text.
   *
   * The function checks if the search text is present in either the product name's finnish translation
   * or the product code. If the search text is not provided, it returns the original
   * array of products.
   */
  const filterBySearch = (optionsToFilter: Product[]) => {
    if (!searchText) return optionsToFilter
    return optionsToFilter.filter(product => {
      const productName = getTranslation(product.translations, 'fi').name.toLowerCase()
      const productCode = product.product_code.toLowerCase()
      const searchTextLower = searchText.toLowerCase()
      return productName.includes(searchTextLower) || productCode.includes(searchTextLower)
    })
  }

  const filterByProductGroup = (optionsToFilter: Product[]) => {
    if (!productGroupFilter) return optionsToFilter
    return optionsToFilter.filter(product => product.product_group === productGroupFilter)
  }

  const filterOptions = (optionsToFilter: Product[]) => {
    const optionsFilteredBySearch = filterBySearch(optionsToFilter)
    const optionsFilteredByProductGroup = filterByProductGroup(optionsFilteredBySearch)
    return optionsFilteredByProductGroup
  }

  return {
    searchText,
    productGroupFilter,
    productGroupOptions,
    isProductGroupParameterGroupLoading,
    handleSearchTextChange,
    handleProductGroupFilterChange,
    filterOptions
  }
}

export default useProductsEdit
